import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { useQuery, useLazyQuery, useMutation } from '@apollo/client'
import styles from './MediaView.module.scss'
import {
  GET_MEDIA_LIBRARY_QUERY,
  GET_PROJECTS_QUERY,
  ATTACH_MEDIA_ASSETS_MUTATION,
  CREATE_UPLOAD_GRANT_MUTATION,
  GET_SELECTION_DOWNLOAD_QUERY,
} from '../query/gqlQueries'
import { uploadFile, runIngest, CHUNK_THRESHOLD, INBOX_PATH } from '../uploadMedia'

import CloseIcon from '@mui/icons-material/Close'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import RefreshIcon from '@mui/icons-material/Refresh'
import LinkIcon from '@mui/icons-material/Link'
import VideocamIcon from '@mui/icons-material/Videocam'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera'
import CameraIcon from '@mui/icons-material/Camera'
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone'
import VideoCameraBackIcon from '@mui/icons-material/VideoCameraBack'
import FlightIcon from '@mui/icons-material/Flight'
import DevicesOtherIcon from '@mui/icons-material/DevicesOther'
import DownloadIcon from '@mui/icons-material/Download'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import VisibilityIcon from '@mui/icons-material/Visibility'
import PlayCircleIcon from '@mui/icons-material/PlayCircle'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

// The devices media can be imported from. Fixed rather than free text: a typed
// name drifts ("Sony" / "sony" / "a7iv") and every spelling becomes its own
// folder under _library/<date>/ and its own group in the listing.
//
// `code` is the folder name written to disk, so it stays lowercase and stable;
// `label` is only ever displayed. Adding a camera later means adding a row
// here — nothing on the server hard-codes this list.
// Roughly how many assets to pull per page. A target, not a hard count: pages
// are whole days, so the last day taken usually carries past it.
const PAGE_SIZE = 1000

const DEVICES = [
  { code: 'sony', label: 'Sony', Icon: PhotoCameraIcon },
  { code: 'fujifilm', label: 'Fujifilm', Icon: CameraIcon },
  { code: 'iphone', label: 'iPhone', Icon: PhoneIphoneIcon },
  { code: 'gopro', label: 'GoPro', Icon: VideoCameraBackIcon },
  { code: 'drone', label: 'Drone', Icon: FlightIcon },
]

const DEVICE_BY_CODE = DEVICES.reduce((m, d) => { m[d.code] = d; return m }, {})

// Folders can also appear from outside the app — an SMB card dump, a manual
// mkdir — carrying any name at all. Those still render, under their raw folder
// name with a neutral icon, rather than disappearing from the library.
function deviceMeta(code) {
  const key = String(code || '').toLowerCase()
  return DEVICE_BY_CODE[key] || { code: key, label: code || 'Unknown', Icon: DevicesOtherIcon }
}

// Rewrite the server-baked media origin to the client's image host — same
// helper as MediaView. Path and query are preserved verbatim: the signature
// lives in the query string, so stripping it would make every URL 403.
const IMAGE_STORE_URI = process.env.REACT_APP_IMAGE_STORE_URI

function storeUrl(url) {
  if (!url) return url
  if (!IMAGE_STORE_URI) return url
  try {
    const u = new URL(url)
    return IMAGE_STORE_URI.replace(/\/$/, '') + u.pathname + u.search
  } catch { return url }
}

function formatBytes(bytes) {
  if (!bytes || bytes < 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let i = 0, n = bytes
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++ }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 2)} ${units[i]}`
}

// "2026-07-17" → "17 Jul 2026"
function prettyDate(d) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d || '')) return d
  const [y, m, day] = d.split('-').map(Number)
  return new Date(y, m - 1, day).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

// Two modes, one component. Normally this browses the originals and attaches
// a selection to a project. In `pickMode` the same selection UI is used to
// group originals under an open shoot instead — the grid, the device headers
// and the select-all behaviour are identical, only the bottom bar differs.
export default function MediaLibrary({
  username,
  onBack,
  setIsVisible,
  pickMode = false,
  pickLabel = '',
  onPick,
}) {
  // The library is 8000+ files and still growing. Loading it whole made the
  // server stat and merge every one of them on every request, and the browser
  // mount every tile. Pages are whole days, newest first, roughly PAGE_SIZE
  // assets at a time — whole days because the grid is grouped by date and a
  // boundary through the middle of one would show the same day twice.
  const [pages, setPages] = useState([])
  const [cursor, setCursor] = useState(null)
  const [totals, setTotals] = useState({ totalAssets: 0, totalDays: 0 })
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState(null)

  const [fetchLibrary] = useLazyQuery(GET_MEDIA_LIBRARY_QUERY, { fetchPolicy: 'network-only' })

  const loadPage = useCallback(async ({ reset = false } = {}) => {
    if (!username) return
    setLoading(true)
    setLoadError(null)
    try {
      const { data } = await fetchLibrary({
        variables: { username, limit: PAGE_SIZE, cursor: reset ? null : cursor },
      })
      const page = data?.getMediaLibrary
      if (!page) return
      setPages((prev) => (reset ? [page.days] : [...prev, page.days]))
      setCursor(page.nextCursor || null)
      setTotals({ totalAssets: page.totalAssets, totalDays: page.totalDays })
    } catch (e) {
      setLoadError(e.message || 'Could not load the library')
    } finally {
      setLoading(false)
    }
  }, [username, cursor, fetchLibrary])

  // First page on mount, and again whenever the signed-in user changes.
  useEffect(() => {
    if (username) loadPage({ reset: true })
    // Deliberately keyed on username alone: loadPage changes identity with the
    // cursor, so depending on it here would re-fetch page one every time a
    // later page loaded.
  }, [username])

  const refetch = useCallback(() => loadPage({ reset: true }), [loadPage])

  const { data: projectsData } = useQuery(GET_PROJECTS_QUERY, { variables: { username }, skip: !username })
  const [attachAssets, { loading: attaching }] = useMutation(ATTACH_MEDIA_ASSETS_MUTATION)
  const [createUploadGrant] = useMutation(CREATE_UPLOAD_GRANT_MUTATION)

  const days = useMemo(() => pages.flat(), [pages])
  const shownAssets = useMemo(
    () => days.reduce((n, d) => n + d.devices.reduce((m, dev) => m + dev.assets.length, 0), 0),
    [days]
  )
  const hasMore = cursor !== null
  const existingProjects = projectsData?.getProjects || []

  const [selected, setSelected] = useState(new Set())
  const [attachProject, setAttachProject] = useState('')

  // Import panel: upload from anywhere into _inbox/, which the server then
  // sorts into _library/<capture date>/<device>/ by reading each file.
  const [importOpen, setImportOpen] = useState(false)
  const [importDevice, setImportDevice] = useState('')
  const [importing, setImporting] = useState(false)
  const [importMsg, setImportMsg] = useState(null)
  const importInputRef = useRef(null)

  // Signed permission to write into one folder, refreshed when nearly spent.
  // The image-server no longer accepts a shared token, so an import can't
  // start without one. See the identical helper in MediaView.
  const grantRef = useRef(null)
  const getUploadUrl = useCallback(async (rel) => {
    const cached = grantRef.current
    if (cached && cached.path === rel && cached.expiresAtMs - Date.now() > 120000) {
      return cached.url
    }
    const { data } = await createUploadGrant({ variables: { path: rel } })
    const grant = data?.createUploadGrant
    if (!grant?.url) throw new Error('Could not get permission to upload here')
    grantRef.current = {
      path: rel,
      url: storeUrl(grant.url),
      expiresAtMs: new Date(grant.expiresAt).getTime(),
    }
    return grantRef.current.url
  }, [createUploadGrant])

  const handleImport = useCallback(async (fileList) => {
    const files = Array.from(fileList || [])
    if (files.length === 0) return
    if (!IMAGE_STORE_URI) { window.alert('REACT_APP_IMAGE_STORE_URI is not set — cannot reach the media server.'); return }

    // Everything lands in the inbox first; the server then reads each file's
    // capture date and camera and files it into the library. Nothing here
    // decides where a file belongs — the file itself does.
    const rel = INBOX_PATH

    setImporting(true)
    let ok = 0
    const failed = []
    for (let i = 0; i < files.length; i++) {
      const f = files[i]
      const label = `${i + 1}/${files.length} — ${f.name}`
      setImportMsg(`Uploading ${label}…`)
      try {
        // uploadFile slices anything large into sub-100 MB requests, so the
        // tunnel's per-request cap no longer limits file size. There is no
        // size pre-flight any more — a 7 GB clip is simply more chunks.
        await uploadFile(f, () => getUploadUrl(rel), (frac) => {
          setImportMsg(
            f.size > CHUNK_THRESHOLD && frac < 1
              ? `Uploading ${label} — ${Math.round(frac * 100)}%`
              : `Uploading ${label}…`
          )
        })
        ok++
      } catch (e) {
        const m = String(e.message)
        failed.push(
          `${f.name} — ` +
          (m.includes('Load failed') || m.includes('Failed to fetch')
            ? 'connection lost — the server may be unreachable'
            : m)
        )
      }
    }

    if (importInputRef.current) importInputRef.current.value = ''

    // Now file what landed: hash, read capture date and camera, deduplicate,
    // move into the library. Reported before refreshing, because a refetch
    // that throws used to take the result message with it and leave the panel
    // frozen on the last "Uploading n/n" line.
    let summary = `Uploaded ${ok}/${files.length}`
    if (ok > 0) {
      setImportMsg(`Uploaded ${ok} — sorting by capture date…`)
      try {
        const r = await runIngest(await getUploadUrl(rel), importDevice || null)
        const bits = [`filed ${r.filed}`]
        if (r.duplicates) bits.push(`${r.duplicates} already in the library`)
        if (r.errors) bits.push(`${r.errors} error${r.errors === 1 ? '' : 's'}`)
        summary = bits.join(' · ')
      } catch (e) {
        summary = `Uploaded ${ok}, but sorting failed: ${e.message}`
      }
    }
    if (failed.length) summary += ` · ${failed.length} upload${failed.length === 1 ? '' : 's'} failed`
    setImporting(false)
    setImportMsg(summary)

    try {
      await refetch()
    } catch (e) {
      setImportMsg((prev) => `${prev} · couldn't refresh the listing: ${e.message}`)
    }

    if (failed.length > 0) {
      window.alert(`${ok} uploaded, ${failed.length} failed:\n\n${failed.join('\n')}`)
    }
  }, [importDevice, refetch, getUploadUrl])

  const toggle = useCallback((path) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(path)) next.delete(path); else next.add(path)
      return next
    })
  }, [])

  const selectDevice = useCallback((assets) => {
    setSelected((prev) => {
      const next = new Set(prev)
      const allSelected = assets.every((a) => next.has(a.path))
      assets.forEach((a) => { allSelected ? next.delete(a.path) : next.add(a.path) })
      return next
    })
  }, [])

  const clearSelection = () => setSelected(new Set())

  // One click cannot mean two things. In browse mode it opens the preview; in
  // select mode it adds to the selection. Picking a mode explicitly beats
  // modifier keys, which are invisible and don't exist on an iPad.
  const [mode, setMode] = useState('browse')
  const selectMode = pickMode || mode === 'select'
  useEffect(() => { if (!selectMode) clearSelection() }, [selectMode])

  // Flat, in the order the grid renders, so the preview's arrows walk the whole
  // loaded library rather than stopping at the end of a device group.
  const flatAssets = useMemo(() => {
    const out = []
    for (const d of days) for (const dev of d.devices) for (const a of dev.assets) out.push(a)
    return out
  }, [days])
  const flatIndex = useMemo(
    () => new Map(flatAssets.map((a, i) => [a.path, i])),
    [flatAssets]
  )

  const [previewIdx, setPreviewIdx] = useState(null)
  const previewed = previewIdx === null ? null : flatAssets[previewIdx]

  useEffect(() => {
    if (previewIdx === null) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') setPreviewIdx(null)
      else if (e.key === 'ArrowRight') setPreviewIdx((i) => (i === null ? i : Math.min(i + 1, flatAssets.length - 1)))
      else if (e.key === 'ArrowLeft') setPreviewIdx((i) => (i === null ? i : Math.max(i - 1, 0)))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [previewIdx, flatAssets.length])

  // Download. Files go straight to the browser, one after another.
  //
  // The URLs come from getSelectionDownload rather than the ones already on
  // screen: a browsing URL is signed for 30 minutes, and working through a
  // large selection can outlast that, so the last files would 403. These are
  // signed for 12h.
  //
  // Sequential with a gap, not all at once: a browser cancels a burst of
  // simultaneous downloads, and Chrome asks once for permission to save
  // several files. A curl manifest is still offered for very large selections,
  // where resuming actually matters.
  const [downloading, setDownloading] = useState(false)
  const [downloadProgress, setDownloadProgress] = useState(null)
  const [downloadHint, setDownloadHint] = useState(null)
  const [fetchSelectionDownload] = useLazyQuery(GET_SELECTION_DOWNLOAD_QUERY, {
    fetchPolicy: 'network-only',
  })

  const saveOne = useCallback((url, filename) => {
    const a = document.createElement('a')
    a.href = storeUrl(url)
    a.download = filename
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }, [])

  const handleDownloadSelected = useCallback(async () => {
    if (selected.size === 0) return
    setDownloading(true)
    setDownloadHint(null)
    try {
      const { data } = await fetchSelectionDownload({ variables: { paths: Array.from(selected) } })
      const dl = data?.getSelectionDownload
      if (!dl || !dl.files.length) { window.alert('Nothing to download in that selection.'); return }

      for (let i = 0; i < dl.files.length; i++) {
        const f = dl.files[i]
        setDownloadProgress({ done: i, total: dl.files.length, name: f.filename })
        saveOne(f.url, f.filename)
        // Enough of a gap that the browser treats these as separate saves
        // rather than cancelling the ones still queued behind it.
        if (i < dl.files.length - 1) {
          await new Promise((r) => setTimeout(r, 400))
        }
      }
      setDownloadProgress(null)
      setDownloadHint({
        count: dl.files.length,
        gb: (dl.totalBytes / 1073741824).toFixed(2),
      })
    } catch (err) {
      window.alert(err.message || 'Could not start the download')
    } finally {
      setDownloading(false)
      setDownloadProgress(null)
    }
  }, [selected, fetchSelectionDownload, saveOne])

  // For a selection big enough that a failure halfway through matters. curl
  // resumes, downloads in parallel and skips what is already there; a browser
  // does none of that.
  const handleDownloadManifest = useCallback(async () => {
    if (selected.size === 0) return
    setDownloading(true)
    try {
      const { data } = await fetchSelectionDownload({ variables: { paths: Array.from(selected) } })
      const dl = data?.getSelectionDownload
      if (!dl || !dl.files.length) return
      const gb = (dl.totalBytes / 1073741824).toFixed(2)
      const lines = [
        `# ${dl.fileCount} files, ${gb} GB — selected from the library`,
        `# Links expire ${new Date(dl.expiresAt).toLocaleString()}.`,
        '#',
        '#     curl --parallel --parallel-max 4 -C - --create-dirs -K THIS_FILE',
        '',
      ]
      for (const f of dl.files) {
        lines.push(`url = "${storeUrl(f.url)}"`)
        lines.push(`output = "${f.output}"`)
        lines.push('')
      }
      const blob = new Blob([lines.join('\n')], { type: 'text/plain' })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `library-selection-${dl.fileCount}-files.curl`
      document.body.appendChild(a); a.click(); document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(a.href), 10000)
      setDownloadHint({ manifest: a.download, count: dl.fileCount, gb })
    } finally {
      setDownloading(false)
    }
  }, [selected, fetchSelectionDownload])


  const handleAttach = async () => {
    if (selected.size === 0 || !attachProject.trim()) return
    try {
      await attachAssets({
        variables: { paths: Array.from(selected), project: attachProject.trim(), username },
      })
      clearSelection()
      await refetch()
    } catch (err) {
      console.error('Attach failed:', err)
      window.alert(err.message || 'Failed to attach')
    }
  }

  return (
    <div className={styles.view}>
      <div className={styles.gridBg} />
      <button className={styles.closeBtn} onClick={() => setIsVisible(false)}><CloseIcon /></button>

      <div className={styles.container} style={{ paddingBottom: selected.size > 0 ? '5rem' : undefined }}>
        <header className={styles.header}>
          <button className={styles.backBtn} onClick={onBack}>
            <ArrowBackIcon fontSize="small" /> {pickMode ? 'Cancel' : 'Shoots'}
          </button>
          <div className={styles.titleGroup}>
            <div className={styles.label}>
              {pickMode ? `Pick originals for “${pickLabel}”` : 'Media · originals'}
            </div>
            <h1 className={styles.title}>Library</h1>
          </div>
          <div className={styles.toolbar}>
            {/* Hidden in pickMode: the whole point of that mode is picking, so
                offering a browse mode that cannot pick would just be a trap. */}
            {!pickMode && (
              <button
                className={`${styles.iconBtn} ${selectMode ? styles.primaryBtn : ''}`}
                title={selectMode ? 'Click a photo to select it' : 'Click a photo to preview it'}
                onClick={() => setMode((m) => (m === 'select' ? 'browse' : 'select'))}
              >
                {selectMode
                  ? <><CheckBoxIcon fontSize="small" /> Selecting</>
                  : <><VisibilityIcon fontSize="small" /> Browsing</>}
              </button>
            )}
            <button
              className={`${styles.iconBtn} ${importOpen ? styles.primaryBtn : ''}`}
              title="Import files into the library"
              onClick={() => setImportOpen((v) => !v)}
            >
              <FileUploadIcon fontSize="small" /> Import
            </button>
            <button className={styles.iconBtn} title="Rescan" onClick={() => refetch()}>
              <RefreshIcon fontSize="small" />
            </button>
          </div>
        </header>

        {importOpen && (
          <div className={styles.importPanel}>
            <div className={styles.importFields}>
              <button
                className={`${styles.iconBtn} ${styles.primaryBtn}`}
                disabled={importing}
                onClick={() => importInputRef.current && importInputRef.current.click()}
              >
                <FileUploadIcon fontSize="small" /> {importing ? 'Working…' : 'Choose files…'}
              </button>
              <input
                ref={importInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => handleImport(e.target.files)}
              />
              {/* No date field: the server reads each file's capture date from
                  its EXIF. A card usually spans several days, so picking one
                  date by hand was wrong for most of what was on it. Device is
                  only a fallback for files carrying no usable metadata. */}
              <div className={styles.importField}>
                <span>If a file has no camera info, treat it as</span>
                <div className={styles.devicePicker} role="group" aria-label="Fallback device">
                  {DEVICES.map(({ code, label, Icon }) => {
                    const active = importDevice === code
                    return (
                      <button
                        key={code}
                        type="button"
                        className={`${styles.deviceBtn} ${active ? styles.deviceBtnActive : ''}`}
                        aria-pressed={active}
                        onClick={() => setImportDevice(active ? '' : code)}
                      >
                        <Icon fontSize="small" />
                        <span>{label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
            <div className={styles.importHint}>
              Files are sorted automatically by the date they were <strong>taken</strong> and the camera
              that took them — <code>_library/&lt;capture date&gt;/&lt;device&gt;/</code>. Anything already
              in the library is recognised by content and skipped, so re-importing a card is safe.
              Any size: large files upload in parts and resume if interrupted.
              A whole card is still faster over SMB (<code>smb://192.168.1.215</code>) — drop it in
              <code>_inbox/</code> and it files itself.
            </div>
            {importMsg && <div className={styles.importMsg}>{importMsg}</div>}
          </div>
        )}

        {loading && days.length === 0 && <div className={styles.status}>Scanning the library…</div>}

        {!loading && days.length === 0 && (
          <div className={styles.emptyState}>
            <p>No originals in the library yet.</p>
            <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
              Offload a card into <code>/Volumes/dataStoreA/media/_library/&lt;date&gt;/&lt;device&gt;/</code> on the mini, then Rescan.
            </p>
          </div>
        )}

        {days.map((day) => (
          <div key={day.date} className={styles.libDay}>
            <div className={styles.libDayHeader}>{prettyDate(day.date)}</div>
            {day.devices.map((dev) => {
              const { label: devLabel, Icon: DevIcon } = deviceMeta(dev.device)
              return (
              <div key={dev.device} className={styles.libDevice}>
                <div className={styles.libDeviceHeader}>
                  <span className={styles.libDeviceName}>
                    <DevIcon fontSize="small" />
                    {devLabel} · {dev.assets.length}
                  </span>
                  {selectMode && (
                    <button className={styles.libSelectAll} onClick={() => selectDevice(dev.assets)}>
                    {dev.assets.every((a) => selected.has(a.path)) ? 'deselect all' : 'select all'}
                    </button>
                  )}
                </div>
                <div className={styles.assetGrid}>
                  {dev.assets.map((a) => {
                    const isSel = selected.has(a.path)
                    return (
                      <div
                        key={a.path}
                        className={`${styles.assetCard} ${isSel ? styles.assetCardSelected : ''}`}
                        onClick={() => (selectMode
                          ? toggle(a.path)
                          : setPreviewIdx(flatIndex.get(a.path) ?? null))}
                      >
                        <div className={styles.assetThumb}>
                          {a.thumbnailUrl ? (
                            // See MediaView: a failed load dims the tile, and without this
                            // reset it never brightens again once the thumbnail arrives.
                            <img src={storeUrl(a.thumbnailUrl)} alt={a.filename} loading="lazy"
                              onLoad={(e) => { e.currentTarget.style.opacity = '' }}
                              onError={(e) => { e.currentTarget.style.opacity = 0.2 }} />
                          ) : (
                            <div className={styles.assetThumbPlaceholder}>
                              {a.kind === 'video' ? <VideocamIcon fontSize="small" /> : <InsertDriveFileIcon fontSize="small" />}
                              <span>{a.kind}</span>
                            </div>
                          )}
                          {a.kind === 'video' && a.thumbnailUrl && (
                            <div className={styles.playOverlay}><PlayCircleIcon /></div>
                          )}
                          {isSel && <div className={styles.assetCheck}>✓</div>}
                        </div>
                        <div className={styles.assetMeta}>
                          <div className={styles.assetName} title={a.filename}>{a.filename}</div>
                          <div className={styles.assetStats}>{formatBytes(a.size)}</div>
                          {a.projects && a.projects.length > 0 && (
                            <div className={styles.assetProjects}>
                              {a.projects.map((p) => <span key={p} className={styles.assetProjectTag}>{p}</span>)}
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
              )
            })}
          </div>
        ))}

        {loadError && (
          <div className={styles.status}>{loadError}</div>
        )}

        {/* Whole days per page, so this count moves in uneven steps. Showing
            both numbers makes that legible instead of looking like a bug. */}
        {days.length > 0 && (
          <div className={styles.libMore}>
            <span className={styles.libMoreCount}>
              {shownAssets.toLocaleString()} of {totals.totalAssets.toLocaleString()} files
              {totals.totalDays > 0 && ` · ${days.length} of ${totals.totalDays} days`}
            </span>
            {hasMore ? (
              <button
                className={`${styles.iconBtn} ${styles.primaryBtn}`}
                disabled={loading}
                onClick={() => loadPage()}
              >
                {loading ? 'Loading…' : `Load ${PAGE_SIZE.toLocaleString()} more`}
              </button>
            ) : (
              <span className={styles.libMoreDone}>everything loaded</span>
            )}
          </div>
        )}
      </div>

      {selected.size > 0 && pickMode && (
        <div className={styles.attachBar}>
          <span className={styles.attachCount}>{selected.size} selected</span>
          <span className={styles.attachHint}>
            Grouped under <strong>{pickLabel}</strong> — the files stay in the library.
          </span>
          <button
            className={`${styles.iconBtn} ${styles.primaryBtn}`}
            onClick={() => onPick && onPick(Array.from(selected))}
          >
            <LinkIcon fontSize="small" /> Add to shoot
          </button>
          <button className={styles.iconBtn} onClick={clearSelection}>Clear</button>
        </div>
      )}

      {selected.size > 0 && !pickMode && (
        <div className={styles.attachBar}>
          <span className={styles.attachCount}>{selected.size} selected</span>
          {/* A select, not a text field with a datalist: a datalist only
              suggests, so a mistyped name still went through and created a
              near-duplicate project. Media is filed against work that already
              exists; new projects are created in the Work view. */}
          <select
            className={styles.attachSelect}
            value={attachProject}
            onChange={(e) => setAttachProject(e.target.value)}
            aria-label="Attach to project"
          >
            <option value="">
              {existingProjects.length ? 'Attach to project…' : 'No projects yet'}
            </option>
            {existingProjects.map((p) => (
              <option key={p.identity} value={p.properties.title}>
                {p.properties.title}
              </option>
            ))}
          </select>
          <button
            className={`${styles.iconBtn} ${styles.primaryBtn}`}
            onClick={handleAttach}
            disabled={attaching || !attachProject}
          >
            <LinkIcon fontSize="small" /> {attaching ? 'Attaching…' : 'Attach'}
          </button>
          <button
            className={`${styles.iconBtn} ${styles.primaryBtn}`}
            onClick={handleDownloadSelected}
            disabled={downloading}
            title="Saves the selected files to your Downloads folder"
          >
            <DownloadIcon fontSize="small" />{' '}
            {downloadProgress
              ? `Downloading ${downloadProgress.done + 1} of ${downloadProgress.total}…`
              : downloading ? 'Preparing…' : `Download ${selected.size}`}
          </button>
          {/* Only past the point where a failure halfway through actually costs
              something. curl resumes and skips what is already there. */}
          {selected.size > 25 && (
            <button
              className={styles.iconBtn}
              onClick={handleDownloadManifest}
              disabled={downloading}
              title="A resumable curl manifest instead — better for very large pulls"
            >
              curl list
            </button>
          )}
          <button className={styles.iconBtn} onClick={clearSelection}>Clear</button>
        </div>
      )}

      {downloadHint && (
        <div className={styles.attachBar}>
          {downloadHint.manifest ? (
            <span className={styles.attachHint}>
              Saved <strong>{downloadHint.manifest}</strong> — {downloadHint.count} files, {downloadHint.gb} GB.
              In the folder you want them in, run{' '}
              <code>curl --parallel --parallel-max 4 -C - --create-dirs -K {downloadHint.manifest}</code>
            </span>
          ) : (
            <span className={styles.attachHint}>
              Sent <strong>{downloadHint.count}</strong> file{downloadHint.count === 1 ? '' : 's'} to
              your Downloads folder — {downloadHint.gb} GB.
            </span>
          )}
          <button className={styles.iconBtn} onClick={() => setDownloadHint(null)}>Dismiss</button>
        </div>
      )}

      {previewed && (
        <div className={styles.lightbox} onClick={() => setPreviewIdx(null)}>
          <button className={styles.lightboxClose} onClick={() => setPreviewIdx(null)} title="Close (Esc)">
            <CloseIcon />
          </button>
          {previewIdx > 0 && (
            <button
              className={`${styles.lightboxNav} ${styles.lightboxPrev}`}
              onClick={(e) => { e.stopPropagation(); setPreviewIdx(previewIdx - 1) }}
              title="Previous (←)"
            ><ChevronLeftIcon /></button>
          )}
          <div className={styles.lightboxInner} onClick={(e) => e.stopPropagation()}>
            {previewed.kind === 'video' ? (
              <video
                key={previewed.path}
                className={styles.lightboxVideo}
                src={storeUrl(previewed.fileUrl)}
                poster={previewed.thumbnailUrl ? storeUrl(previewed.thumbnailUrl) : undefined}
                controls
                autoPlay
              />
            ) : previewed.thumbnailUrl ? (
              // The 800px thumbnail, not the original: a raw is 65 MB and no
              // browser can decode an ARW anyway.
              <img src={storeUrl(previewed.thumbnailUrl)} alt={previewed.filename} />
            ) : (
              <div className={styles.lightboxNoPreview}>No preview for this file type</div>
            )}
            <div className={styles.lightboxCaption}>
              <span>
                {previewed.filename} · {formatBytes(previewed.size)} · {previewIdx + 1} / {flatAssets.length}
              </span>
              <a
                className={styles.iconBtn}
                href={storeUrl(previewed.downloadUrl)}
                download={previewed.filename}
                onClick={(e) => e.stopPropagation()}
              >
                <DownloadIcon fontSize="small" /> Download
              </a>
              {previewed.sidecars && previewed.sidecars.length > 0 && (
                <span className={styles.lightboxSidecars}>
                  {previewed.sidecars.map((sc) => (
                    <a key={sc.path} href={storeUrl(sc.downloadUrl)} download={sc.filename}
                       onClick={(e) => e.stopPropagation()}>{sc.filename}</a>
                  ))}
                </span>
              )}
            </div>
          </div>
          {previewIdx < flatAssets.length - 1 && (
            <button
              className={`${styles.lightboxNav} ${styles.lightboxNext}`}
              onClick={(e) => { e.stopPropagation(); setPreviewIdx(previewIdx + 1) }}
              title="Next (→)"
            ><ChevronRightIcon /></button>
          )}
        </div>
      )}
    </div>

  )
}
